// §5.4 — macOS window semantics.
//
// This is the policy that makes the `WindowManagerPolicy` seam worth having: it is the one skin
// whose **green button does not maximize**. D-032 and §5.5 say the button moves the window into a
// dedicated Space — a desktop containing exactly that window — and the kernel already has the
// primitive for it, because §5.5 gives desktops to every skin and this one just uses them
// differently.
//
// The rest follows from the platform. There is no Snap Assist here (macOS has never had it), the
// switcher is a Spaces model rather than a window grid, and the chrome is on the **left**.
import type { Edge, SnapLayout, SnapZone, WindowManagerPolicy } from '@hangar/shared';

export { zoneRect } from '@/kernel/wm/geometry';

/**
 * §5.4 — macOS tiling is halves and full, and nothing else.
 *
 * Ventura's "Tile Window to Left/Right of Screen" is the whole of it, so the picker offers the
 * whole of it. Inventing quadrants here to match the Windows skin would be inventing a platform.
 */
export const MACOS_LAYOUTS: readonly SnapLayout[] = [
  {
    id: 'halves',
    label: 'Tile left and right',
    zones: [
      { id: 'halves-left', rect: { x: 0, y: 0, w: 0.5, h: 1 } },
      { id: 'halves-right', rect: { x: 0.5, y: 0, w: 0.5, h: 1 } },
    ],
  },
  {
    id: 'full',
    label: 'Fill',
    zones: [{ id: 'full', rect: { x: 0, y: 0, w: 1, h: 1 } }],
  },
];

function zone(id: string): SnapZone | null {
  for (const layout of MACOS_LAYOUTS) {
    const found = layout.zones.find((candidate) => candidate.id === id);
    if (found) return found;
  }
  return null;
}

/**
 * §5.4 — what each display edge means here.
 *
 * The **top edge does nothing**, which is the deliberate difference from the Windows skin: on this
 * platform the top of the screen is the menu bar, and dragging a window there has never maximized
 * it. Corners do nothing either, for the same reason there are no quadrants.
 */
const EDGE_ZONES: Record<Edge, string | null> = {
  left: 'halves-left',
  right: 'halves-right',
  top: null,
  bottom: null,
  topLeft: null,
  topRight: null,
  bottomLeft: null,
  bottomRight: null,
};

export const macosPolicy: WindowManagerPolicy = {
  // D-032 — the one that matters. §5.5's dedicated Space, not a maximize.
  onMaximizeButton: () => 'enterFullscreenSpace',

  onEdgeDrag(edge) {
    const id = EDGE_ZONES[edge];
    return id === null ? null : zone(id);
  },

  snapZones: () => MACOS_LAYOUTS.map((layout) => ({ ...layout, zones: [...layout.zones] })),

  // §6.3 — Mission Control: a Spaces strip above an exposé of the windows on the active Space.
  switcherModel: () => 'spaces',
  minimizeTarget: () => 'barSlot',
  // §6.3 — traffic lights on the left. The one visible thing every screenshot of this OS shows.
  chromeSide: () => 'left',
  snapAssist: () => false,
};
